// Gdzie dziecko jest w aplikacji — żeby odświeżenie strony zostawiało je na tym samym ekranie (patch-34).
//
// Zapis żyje w pamięci SESJI przeglądarki (sessionStorage): przetrwa odświeżenie karty, ale nie przetrwa zamknięcia aplikacji.
// Dlatego świeże uruchomienie zawsze zaczyna od wyboru profilu (z PIN-em) i powitania, a odświeżenie wraca dokładnie tam, gdzie dziecko było.
// „Wyloguj” i „Zmień profil” kasują sesję, więc po nich znów jest wybór profilu i powitanie „Zaczynamy”.

/** Ekran, na który wracamy po odświeżeniu. Ekrany tymczasowe wracają jako ich „rodzic”: podsumowanie lekcji jako ekran główny (postęp jest już zapisany), koło nastroju jako start lekcji, a rozmowa z botem (wstrzymana) jako ekran główny — rozmowa nie jest wznawialna. */
export type Route =
  | { screen: 'home' }
  /** „Mój tydzień”; `offset` to tydzień wstecz (0 = bieżący, -1 = poprzedni…). */
  | { screen: 'week'; offset: number }
  | { screen: 'look' }
  /** Ranking graczy (patch-35); `scope` to zakładka: ten tydzień albo wszystkie czasy. */
  | { screen: 'ranking'; scope: 'week' | 'all' }
  /** Panel rodzica: po odświeżeniu pyta o PIN od nowa (PIN żyje tylko w pamięci ekranu). */
  | { screen: 'admin' }
  /**
   * Lekcja kursu (`lesson` = id, np. „a1-001”) albo „Powtórka dnia”. Od patcha 48 nie ma już lekcji „dni”: zapis z takim ekranem (sessionStorage starej wersji
   * aplikacji) wraca na ekran główny, a zapis z dawną powtórką słówek otwiera „Powtórkę dnia” (gdy nic nie czeka — ekran główny). Postęp w lekcji jest w osobnym zapisie (lib/runSnapshot.ts).
   */
  | { screen: 'run'; mode: 'lesson'; lesson: string }
  | { screen: 'run'; mode: 'review' };

export const HOME_ROUTE: Route = { screen: 'home' };

/** Sesja w trybie lokalnym (bez serwera) nie ma profilu — ma ten znacznik zamiast identyfikatora. */
export const LOCAL_PROFILE = '_local';

/** Sesja: kto jest zalogowany w tej karcie, czy widział już powitanie i na jakim jest ekranie. */
export interface SessionState {
  profile: string;
  welcomed: boolean;
  route: Route;
}

const ID = /^[A-Za-z0-9_-]{1,40}$/;
const LESSON_ID = /^[a-z0-9][a-z0-9-]{1,38}$/;
const isInt = (v: unknown, min: number, max: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;

/** Wczytuje ekran z zapisu; wszystko niepoprawne daje ekran główny (zapis mógł pochodzić ze starszej wersji aplikacji). */
export function parseRoute(raw: unknown): Route {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  switch (r.screen) {
    case 'week':
      return isInt(r.offset, -520, 0) ? { screen: 'week', offset: r.offset } : HOME_ROUTE;
    case 'look':
      return { screen: 'look' };
    case 'ranking':
      return { screen: 'ranking', scope: r.scope === 'all' ? 'all' : 'week' };
    case 'admin':
      return { screen: 'admin' };
    case 'run': {
      if (r.mode === 'review') return { screen: 'run', mode: 'review' }; // zapis sprzed patcha 48 (dawna powtórka słówek) też wraca do „Powtórki dnia”; gdy nic nie czeka, ekran główny
      if (r.mode === 'lesson' && typeof r.lesson === 'string' && LESSON_ID.test(r.lesson)) return { screen: 'run', mode: 'lesson', lesson: r.lesson };
      return HOME_ROUTE;
    }
    default:
      return HOME_ROUTE;
  }
}

export function parseSession(raw: unknown): SessionState | null {
  const r = raw as { profile?: unknown; welcomed?: unknown; route?: unknown } | null;
  if (!r || typeof r !== 'object' || typeof r.profile !== 'string' || !ID.test(r.profile)) return null;
  return { profile: r.profile, welcomed: r.welcomed === true, route: parseRoute(r.route) };
}
